import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Coffee,
  Tag,
  Layers,
  CheckCircle2,
  XCircle,
  Hash,
  Beaker,
  Loader2,
} from "lucide-react";
import {
  useAttributes,
  useCategories,
  useProduct,
  useVariantIngredients,
} from "../api";
import { useIngredients } from "@/features/inventory/api";
import { useSettings } from "@/features/settings/api";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import { PageHeader, PageLoader, PageError } from "@/components/common";
import { calculateMarginPercent, formatCurrency } from "@/lib/utils";
import { ProductDetailsForm } from "../components/ProductDetailsForm";
import { ProductVariantsEditor } from "../components/ProductVariantsEditor";
import { ProductAttributesEditor } from "../components/ProductAttributesEditor";
import { ProductIngredientsEditor } from "../components/ProductIngredientsEditor";

const TABS = [
  "overview",
  "details",
  "variants",
  "attributes",
  "ingredients",
] as const;

type TabValue = (typeof TABS)[number];

function isTabValue(value: string | null): value is TabValue {
  return !!value && (TABS as readonly string[]).includes(value);
}

export function ProductDetailsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: product, isLoading, error, refetch } = useProduct(id);
  const { data: settings } = useSettings();
  const { data: categories } = useCategories({ all: true });
  const { data: attributes } = useAttributes();
  const { data: ingredientsData } = useIngredients({ limit: 500 });
  const ingredientMap = (ingredientsData?.data ?? []).reduce<
    Record<string, { name: string; unit: string }>
  >((acc, ing) => {
    acc[ing.id] = { name: ing.name, unit: ing.unit };

    return acc;
  }, {});
  const currencySymbol = settings?.currency_symbol || "$";

  const tabParam = searchParams.get("tab");
  const activeTab: TabValue = isTabValue(tabParam) ? tabParam : "overview";

  const setActiveTab = (value: string) => {
    const next = new URLSearchParams(searchParams);

    if (value === "overview") {
      next.delete("tab");
    } else {
      next.set("tab", value);
    }

    setSearchParams(next, { replace: true });
  };

  if (isLoading) return <PageLoader />;
  if (error || !product) {
    return (
      <PageError
        message="Failed to load product"
        onRetry={refetch}
      />
    );
  }

  const defaultVariant =
    product.variants?.find((v) => v.isDefault) || product.variants?.[0];
  const sortedVariants = [...(product.variants ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  return (
    <div className="container mx-auto p-4 md:p-6">
      <Button
        variant="ghost"
        size="sm"
        className="mb-2 -ml-2"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="mr-1.5 h-4 w-4" />
        Back
      </Button>
      <PageHeader
        title={product.name}
        description={product.description ?? undefined}
      />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 grid w-full grid-cols-2 sm:inline-grid sm:w-auto sm:grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="variants">Variants</TabsTrigger>
          <TabsTrigger value="attributes">Attributes</TabsTrigger>
          <TabsTrigger value="ingredients">Ingredients</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Edit Details</CardTitle>
              <CardDescription>
                Name, description, category and image for this product.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ProductDetailsForm
                product={product}
                categories={categories ?? []}
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="variants">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Edit Variants</CardTitle>
              <CardDescription>
                Sizes and options sold for this product, each with its own
                selling price and cost to make.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ProductVariantsEditor product={product} />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="attributes">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Edit Attributes</CardTitle>
              <CardDescription>
                Customisation options offered at checkout.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ProductAttributesEditor
                product={product}
                attributes={attributes ?? []}
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="ingredients">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Edit Ingredients</CardTitle>
              <CardDescription>
                What gets deducted from inventory when each variant is sold.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ProductIngredientsEditor product={product} />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="overview">
      <div className="grid gap-4 md:gap-6 lg:grid-cols-3">
        <div className="space-y-4 md:space-y-6 lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-start gap-4 space-y-0">
              <div className="shrink-0">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="h-24 w-24 rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-muted">
                    <Coffee className="h-10 w-10 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={product.isActive ? "default" : "secondary"}>
                    {product.isActive ? (
                      <CheckCircle2 className="mr-1 h-3 w-3" />
                    ) : (
                      <XCircle className="mr-1 h-3 w-3" />
                    )}
                    {product.isActive ? "Active" : "Inactive"}
                  </Badge>
                  {product.category && (
                    <Badge variant="outline">{product.category.name}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Hash className="h-3.5 w-3.5" />
                  <span className="font-mono">{product.sku}</span>
                </div>
                {defaultVariant && (
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(defaultVariant.price, currencySymbol)}
                  </p>
                )}
              </div>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Layers className="h-4 w-4" />
                Variants ({sortedVariants.length})
              </CardTitle>
              <CardDescription>
                Sizes / SKUs available for this product.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sortedVariants.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No variants defined.
                </p>
              ) : (
                <div className="space-y-2">
                  {sortedVariants.map((variant) => (
                    <div
                      key={variant.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
                    >
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{variant.name}</span>
                          {variant.isDefault && (
                            <Badge variant="secondary" className="text-[10px]">
                              Default
                            </Badge>
                          )}
                          {!variant.isActive && (
                            <Badge variant="outline" className="text-[10px]">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p className="font-mono text-xs text-muted-foreground">
                          {variant.sku}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">
                          {formatCurrency(variant.price, currencySymbol)}
                        </p>
                        {variant.costPrice > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Cost:{" "}
                            {formatCurrency(variant.costPrice, currencySymbol)}
                            {(() => {
                              const margin = calculateMarginPercent(
                                variant.price,
                                variant.costPrice,
                              );

                              return margin === null
                                ? null
                                : ` · ${margin.toFixed(0)}% margin`;
                            })()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Beaker className="h-4 w-4" />
                Ingredients
              </CardTitle>
              <CardDescription>
                What gets deducted from inventory when each variant is sold.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sortedVariants.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No variants — add one before configuring ingredients.
                </p>
              ) : (
                <div className="space-y-3">
                  {sortedVariants.map((variant) => (
                    <VariantIngredientsRow
                      key={variant.id}
                      variantId={variant.id}
                      variantName={variant.name}
                      ingredientMap={ingredientMap}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Tag className="h-4 w-4" />
                Attributes ({product.attributes?.length ?? 0})
              </CardTitle>
              <CardDescription>
                Customisation options offered at checkout.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!product.attributes || product.attributes.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No attributes attached.
                </p>
              ) : (
                <div className="space-y-3">
                  {product.attributes.map((attr) => (
                    <div key={attr.id} className="rounded-md border p-3">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="font-medium">{attr.name}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {attr.selectionType === "single"
                            ? "Single choice"
                            : "Multi choice"}
                        </Badge>
                        {attr.isRequired && (
                          <Badge variant="secondary" className="text-[10px]">
                            Required
                          </Badge>
                        )}
                      </div>
                      {attr.options && attr.options.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {attr.options.map((opt) => (
                            <Badge
                              key={opt.id}
                              variant={opt.isDefault ? "default" : "outline"}
                              className="font-normal"
                            >
                              {opt.name}
                              {opt.priceAdjustment !== 0 && (
                                <span className="ml-1 text-[10px] opacity-80">
                                  {opt.priceAdjustment > 0 ? "+" : ""}
                                  {formatCurrency(
                                    opt.priceAdjustment,
                                    currencySymbol,
                                  )}
                                </span>
                              )}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          No options configured.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <DetailRow
                label="Category"
                value={product.category?.name ?? "Uncategorised"}
              />
              <Separator />
              <DetailRow
                label="Has variants"
                value={product.hasVariants ? "Yes" : "No"}
              />
              <Separator />
              <DetailRow
                label="Sort order"
                value={String(product.sortOrder)}
              />
              <Separator />
              <DetailRow
                label="Created"
                value={new Date(product.createdAt).toLocaleString()}
              />
              <DetailRow
                label="Updated"
                value={new Date(product.updatedAt).toLocaleString()}
              />
            </CardContent>
          </Card>
        </div>
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

function VariantIngredientsRow({
  variantId,
  variantName,
  ingredientMap,
}: {
  variantId: string;
  variantName: string;
  ingredientMap: Record<string, { name: string; unit: string }>;
}) {
  const { data, isLoading } = useVariantIngredients(variantId);
  const rows = data ?? [];

  return (
    <div className="rounded-md border p-3">
      <p className="mb-2 font-medium">{variantName}</p>
      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No ingredients linked to this variant.
        </p>
      ) : (
        <ul className="space-y-1 text-sm">
          {rows.map((row) => {
            const info = ingredientMap[row.ingredientId];

            return (
              <li
                key={row.ingredientId}
                className="flex items-center justify-between gap-3"
              >
                <span className="truncate">
                  {info?.name ?? "Unknown ingredient"}
                </span>
                <span className="shrink-0 text-muted-foreground">
                  {row.quantityUsed}
                  {info?.unit ? ` ${info.unit}` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
